import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Presentation, FileBarChart, BookOpen } from "lucide-react";

interface Document {
  title: string;
  type: 'pitch-deck' | 'case-study' | 'whitepaper' | 'report';
  embedUrl: string;
}

interface DocumentViewerProps {
  documents: Document[];
  companyName: string;
}

const getDocumentIcon = (type: string) => {
  switch (type) {
    case 'pitch-deck':
      return <Presentation className="h-4 w-4" />;
    case 'case-study':
      return <FileBarChart className="h-4 w-4" />;
    case 'whitepaper':
      return <BookOpen className="h-4 w-4" />;
    case 'report':
      return <FileText className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
};

const DocumentViewer = ({ documents, companyName }: DocumentViewerProps) => {
  if (!documents || documents.length === 0) return null;

  const defaultValue = documents[0].title.toLowerCase().replace(/\s+/g, '-');

  return (
    <section className="container mx-auto px-4 py-12">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold tracking-tight mb-2">Resources & Presentations</h2>
        <p className="text-muted-foreground mb-8">
          Explore detailed materials about {companyName}
        </p>

        <Tabs defaultValue={defaultValue} className="w-full">
          <TabsList className="grid w-full grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            {documents.map((doc) => (
              <TabsTrigger
                key={doc.title}
                value={doc.title.toLowerCase().replace(/\s+/g, '-')}
                className="flex items-center gap-2"
              >
                {getDocumentIcon(doc.type)}
                <span className="truncate">{doc.title}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {documents.map((doc) => (
            <TabsContent
              key={doc.title}
              value={doc.title.toLowerCase().replace(/\s+/g, '-')}
              className="mt-0"
            >
              <Card>
                <CardContent className="p-0">
                  <div className="relative w-full" style={{ paddingBottom: '75%' }}>
                    <iframe
                      src={doc.embedUrl}
                      className="absolute top-0 left-0 w-full h-full border-0"
                      title={`${doc.title} - ${companyName}`}
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

export default DocumentViewer;
